// FreePBX WebRTC Service - SIP.js ile FreePBX entegrasyonu
import { Inviter, Registerer, RegistererState, SessionState, UserAgent, Invitation, SessionInviteOptions } from 'sip.js';
import { SessionDescriptionHandler } from 'sip.js/lib/platform/web';

type Maybe<T> = T | null;

interface FreePBXConfig {
  extension: string;
  password: string;
  server: string; // ws:// veya wss://
  realm: string;
  displayName?: string;
}

export class FreePBXWebRTCService {
  private userAgent: Maybe<UserAgent> = null;
  private registerer: Maybe<Registerer> = null;
  private currentSession: Maybe<Inviter | Invitation> = null;
  private sessions: Map<string, Inviter | Invitation> = new Map(); // ✨ Multi-line support
  private config: Maybe<FreePBXConfig> = null;
  public localStream: Maybe<MediaStream> = null;
  private remoteAudio: HTMLAudioElement;
  private rtpDebugInterval: Maybe<number> = null;
  private reconnectInterval: Maybe<number> = null;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 10;
  private isManualDisconnect: boolean = false;
  private iceConnectionCheckInterval: Maybe<number> = null;
  private lastIceState: string = 'new';
  
  // ✨ RTP Flow Tracking
  private lastInboundPackets: number = 0;
  private lastOutboundPackets: number = 0;
  private lastRTPCheckTime: number = 0;

  // Callbacks
  public onRegistered?: () => void;
  public onUnregistered?: () => void;
  public onRegistrationFailed?: (error: Error) => void;
  public onReconnecting?: (attempt: number, maxAttempts: number) => void;
  public onIncomingCall?: (callerNumber: string, calledNumber: string, session: Invitation) => void;
  public onCallEstablished?: () => void;
  public onCallEnded?: () => void;
  public onLocalStream?: (stream: MediaStream) => void;
  public onCallFailed?: (error: Error) => void;
  public onRemoteStream?: (stream: MediaStream) => void;

  constructor() {
    // React tarafındaki audio element mount olmadan servis oluşturulabilir.
    // Bu yüzden body'ye manuel node eklemek yerine detached fallback kullanıyoruz.
    this.remoteAudio = (document.getElementById('remoteAudio') as HTMLAudioElement | null) || document.createElement('audio');
    this.remoteAudio.autoplay = true;
    this.remoteAudio.setAttribute('playsinline', 'true');
  }

  private log(...args: any[]) {
    console.log('[FreePBX WebRTC]', ...args);
  }

  public setRemoteAudioElement(element: HTMLAudioElement | null): void {
    if (!element || element === this.remoteAudio) {
      return;
    }

    const activeStream = this.remoteAudio.srcObject;
    this.remoteAudio.srcObject = null;
    this.remoteAudio = element;
    this.remoteAudio.autoplay = true;
    this.remoteAudio.setAttribute('playsinline', 'true');

    if (activeStream) {
      this.remoteAudio.srcObject = activeStream;
      this.remoteAudio.play().catch(e => this.log('⚠️ Error resuming remote audio:', e));
    }
  }

  /**
   * FreePBX'e bağlan ve register ol
   */
  public async connect(config: FreePBXConfig): Promise<void> {
    this.config = config;
    this.log('Connecting to FreePBX:', config.server);
    
    // Reset manual disconnect flag at the start of connection
    this.isManualDisconnect = false;
    this.log('🔄 Reset isManualDisconnect flag to false');

    try {
      // SIP URI oluştur
      const sipUri = `sip:${config.extension}@${config.realm}`;
      const uri = UserAgent.makeURI(sipUri);
      
      if (!uri) {
        throw new Error('Failed to create SIP URI');
      }

      // UserAgent konfigürasyonu
      this.userAgent = new UserAgent({
        uri,
        viaHost: 'pbx.percrm.net',
        transportOptions: {
          server: config.server,
          traceSip: false, // Debug için true yapabilirsiniz
          keepAliveInterval: 15, // WebSocket keepalive her 15 saniyede (daha sık)
          keepAliveDebounce: 5 // Keepalive debounce 5 saniye
        },
        authorizationUsername: config.extension,
        authorizationPassword: config.password,
        displayName: config.displayName || config.extension,
        sessionDescriptionHandlerFactoryOptions: {
          iceGatheringTimeout: 2000, // ⚡️ ICE toplama süresini biraz artır (Daha iyi aday bulması için)
          constraints: {
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              googEchoCancellation: true,
              googAutoGainControl: true,
              googNoiseSuppression: true,
              googHighpassFilter: true
            },
            video: false
          },
          peerConnectionConfiguration: {
            // FreePBX başka sunucuda - kendi STUN'ını kullan + Google STUN backup
            iceServers: [
              { urls: `stun:${config.realm}:3478` }, // FreePBX STUN (178.208.187.233:3478)
              { urls: 'stun:stun.l.google.com:19302' }, // Google STUN backup
              { urls: 'stun:stun1.l.google.com:19302' } // Google STUN backup 2
            ],
            iceCandidatePoolSize: 10, // ICE candidate pool
            iceTransportPolicy: 'all', // Tüm ICE adaylarını kullan
            bundlePolicy: 'balanced', // Tüm media track'leri tek transport üzerinden
            rtcpMuxPolicy: 'require', // RTP ve RTCP aynı port'tan
            iceGatheringPolicy: 'all' // Tüm ICE candidate'leri topla
          }
        },
        delegate: {
          onInvite: (invitation: Invitation) => {
            this.handleIncomingCall(invitation);
          }
        },
        // Session timer ekle - her 90 saniyede bir re-INVITE ile session'ı taze tut
        noAnswerTimeout: 60 // Cevap verilmezse 60 saniye timeout
      });

      // UserAgent'ı başlat
      await this.userAgent.start();
      this.log('UserAgent started');

      // Transport state change listener
      let lastTransportState = 'Disconnected';
      this.userAgent.transport.stateChange.addListener((state) => {
        this.log('🔌 Transport state changed:', lastTransportState, '→', state);
        
        if (state === 'Disconnected' && lastTransportState === 'Connected') {
          // Only reconnect if we were connected and now disconnected
          this.log('⚠️ Transport disconnected unexpectedly');
          this.log('   - isManualDisconnect:', this.isManualDisconnect);
          this.log('   - reconnectAttempts:', this.reconnectAttempts);
          
          if (!this.isManualDisconnect && this.reconnectAttempts === 0) {
            this.log('🔄 Scheduling reconnect after unexpected disconnect...');
            this.scheduleReconnect();
          } else if (this.isManualDisconnect) {
            this.log('✅ Manual disconnect - not reconnecting');
          } else {
            this.log('⚠️ Already reconnecting - skipping duplicate reconnect');
          }
        } else if (state === 'Connected') {
          this.log('✅ Transport connected successfully');
          this.reconnectAttempts = 0; // Reset on successful connection
          this.isManualDisconnect = false; // Reset flag on successful connection
        }
        
        lastTransportState = state;
      });

      // Register işlemi
      await this.register();

    } catch (error) {
      this.log('❌ Connection error:', error);
      
      // Transport error ise hemen reconnect dene
      if (!this.isManualDisconnect) {
        this.log('🔄 Connection failed, scheduling reconnect...');
        this.scheduleReconnect();
      }
      
      if (this.onRegistrationFailed) {
        this.onRegistrationFailed(error as Error);
      }
      throw error;
    }
  }

  /**
   * SIP kayıt
   */
  private async register(): Promise<void> {
    if (!this.userAgent) {
      const error = new Error('UserAgent not initialized - cannot register');
      this.log('❌ register failed:', error.message);
      throw error;
    }

    this.log('Registering...');
    
    // Registerer konfigürasyonu: auto-refresh ile registration expire olmaz
    this.registerer = new Registerer(this.userAgent, {
      expires: 600, // 10 dakika
      refreshFrequency: 80 // Registration expire time'ın %80'inde refresh yap (8 dakika)
    });

    this.registerer.stateChange.addListener((state) => {
      this.log('Registration state:', state);
      
      switch (state) {
        case RegistererState.Initial:
          break;
        case RegistererState.Registered:
          this.log('✅ Registered successfully');
          this.reconnectAttempts = 0; // Reset reconnect attempts on success
          if (this.onRegistered) {
            this.onRegistered();
          }
          break;
        case RegistererState.Unregistered:
          this.log('⚠️ Registration unregistered');
          if (this.onUnregistered) {
            this.onUnregistered();
          }
          // Don't reconnect here - transport listener will handle it
          break;
        case RegistererState.Terminated:
          this.log('❌ Registration terminated');
          // Don't reconnect here - transport listener will handle it
          break;
      }
    });

    try {
      await this.registerer.register();
    } catch (error) {
      this.log('Registration failed:', error);
      if (this.onRegistrationFailed) {
        this.onRegistrationFailed(error as Error);
      }
      throw error;
    }
  }

  /**
   * Gelen çağrıyı işle
   */
  private handleIncomingCall(invitation: Invitation): void {
    this.log('📞 Incoming call from:', invitation.remoteIdentity.uri.user);
    
    // ✨ Multi-line: Store invitation in sessions map (don't auto-reject)
    // If there's already an active session, this new one will be queued
    this.sessions.set(invitation.id, invitation);
    this.log(`✨ Stored invitation in sessions map. Total sessions: ${this.sessions.size}`);
    
    // Debug logları
    this.log('🔍 Invitation object type:', typeof invitation);
    this.log('🔍 Invitation constructor:', invitation.constructor.name);
    this.log('🔍 Invitation has accept method:', typeof invitation.accept);
    
    // ✨ Only set as current if no active session
    if (!this.currentSession) {
      this.currentSession = invitation;
      this.log('✨ Set as current session (no active session)');
    } else {
      this.log('✨ Queued invitation (already have active session)');
    }
    
    this.resetRTPStats(); // ✨ Reset RTP stats for new call
    
    // Call event listeners
    this.setupSessionEventListeners(invitation);

    // Callback çağır
    if (this.onIncomingCall) {
      const callerNumber = invitation.remoteIdentity.uri.user || 'Unknown';
      const calledNumber = invitation.request.to.uri.user || '';
      this.onIncomingCall(callerNumber, calledNumber, invitation);
    }
  }

  /**
   * Çağrıyı kabul et
   */
  public async acceptCall(invitation?: Invitation): Promise<void> {
    this.log('✅ Accepting call');
    
    // Check if UserAgent is still connected
    if (!this.userAgent) {
      const error = new Error('UserAgent not initialized - cannot accept call');
      this.log('❌ acceptCall failed:', error.message);
      if (this.onCallFailed) {
        this.onCallFailed(error);
      }
      throw error;
    }
    
    // Debug log - acceptCall başında
    this.log('🔍 acceptCall - invitation param:', !!invitation);
    this.log('🔍 acceptCall - this.currentSession:', !!this.currentSession);
    
    const sessionToAccept = invitation || this.currentSession;
    
    // Debug log
    if (!sessionToAccept) {
      this.log('❌ acceptCall failed: sessionToAccept is null');
      this.log('❌ this.currentSession is:', this.currentSession);
      throw new Error('No valid invitation to accept');
    }
    
    this.log('🔍 sessionToAccept type:', typeof sessionToAccept);
    this.log('🔍 sessionToAccept constructor:', sessionToAccept.constructor?.name);
    this.log('🔍 sessionToAccept.state:', sessionToAccept.state);
    this.log('🔍 sessionToAccept has accept:', typeof (sessionToAccept as any).accept);
    this.log('🔍 Is Invitation instance:', sessionToAccept instanceof Invitation);

    // accept metodu varsa çağır, instanceof kontrolü yapma
    if (!(sessionToAccept instanceof Invitation)) {
      this.log('❌ Session is not an Invitation instance!');
      throw new Error('Invalid session object - not an Invitation');
    }
    
    try {
      await sessionToAccept.accept({
        sessionDescriptionHandlerOptions: {
          constraints: {
            audio: true,
            video: false
          }
        }
      });

      // ✨ Multi-line: Set as current session when accepted
      this.currentSession = sessionToAccept;
      this.log(`✅ Call accepted and set as current session: ${sessionToAccept.id}`);

      // Remote stream'i al
      this.setupRemoteMedia(sessionToAccept);
      
    } catch (error) {
      this.log('Error accepting call:', error);
      if (this.onCallFailed) {
        this.onCallFailed(error as Error);
      }
    }
  }

  /**
   * Çağrıyı reddet
   */
  public async rejectCall(invitation?: Invitation): Promise<void> {
    this.log('❌ Rejecting call');
    
    const sessionToReject = invitation || this.currentSession;
    if (!sessionToReject || !(sessionToReject instanceof Invitation)) {
      this.log('⚠️ No valid invitation to reject');
      return;
    }
    
    try {
      await sessionToReject.reject();
    } catch (error) {
      this.log('Error rejecting call:', error);
    }
  }

  /**
   * Alias for acceptCall
   */
  public async answer(): Promise<void> {
    return this.acceptCall();
  }

  /**
   * Giden çağrı yap
   */
  public async makeCall(targetNumber: string): Promise<string> {
    if (!this.userAgent) {
      const error = new Error('UserAgent not initialized - please connect first');
      this.log('❌ makeCall failed:', error.message);
      if (this.onCallFailed) {
        this.onCallFailed(error);
      }
      throw error;
    }

    if (!this.config) {
      const error = new Error('No configuration available - please connect first');
      this.log('❌ makeCall failed:', error.message);
      if (this.onCallFailed) {
        this.onCallFailed(error);
      }
      throw error;
    }

    // ✨ Multi-line: Do NOT hang up existing calls automatically
    // if (this.currentSession) { ... }

    this.log('📞 Making call to:', targetNumber);
    this.log(`✨ Current active sessions: ${this.sessions.size}`);

    try {
      // Target URI oluştur
      const targetUri = UserAgent.makeURI(`sip:${targetNumber}@${this.config?.realm}`);
      
      if (!targetUri) {
        throw new Error('Failed to create target URI');
      }

      // Inviter oluştur
      const inviter = new Inviter(this.userAgent, targetUri, {
        sessionDescriptionHandlerOptions: {
          constraints: {
            audio: true,
            video: false
          }
        },
        // Early media için invite options
        earlyMedia: true,
        delegate: {
          onSessionDescriptionHandler: (sdh) => {
            this.log('🎵 Early media - Session Description Handler ready');
            // Early media geldiğinde hemen setup yap
            if (sdh && sdh instanceof SessionDescriptionHandler) {
              const pc = sdh.peerConnection;
              if (pc) {
                pc.ontrack = (event) => {
                  this.log('🎵 Early media track received:', event.track.kind);
                  if (event.track && event.streams[0]) {
                    this.remoteAudio.srcObject = event.streams[0];
                    this.remoteAudio.play().catch(e => this.log('Error playing early media:', e));
                  }
                };
              }
            }
          }
        }
      });

      // ✨ Store session in map
      this.sessions.set(inviter.id, inviter);
      this.log(`✅ Session created: ${inviter.id}`);
      
      // ✨ Multi-line: Only set as current if no active session
      if (!this.currentSession || this.currentSession.state === SessionState.Terminated) {
        this.currentSession = inviter;
        this.log('✨ Set as current session (no active session)');
      } else {
        this.log(`✨ Queued outbound call (already have active session: ${this.currentSession.id})`);
      }
      
      this.resetRTPStats(); // ✨ Reset RTP stats for new call
      
      // Event listeners
      this.setupSessionEventListeners(inviter);

      // Çağrıyı başlat
      await inviter.invite();
      
      this.log('Call invitation sent');
      
      return inviter.id;

    } catch (error) {
      this.log('Error making call:', error);
      if (this.onCallFailed) {
        this.onCallFailed(error as Error);
      }
      throw error;
    }
  }

  /**
   * Çağrıyı sonlandır
   */
  public async hangup(callId?: string): Promise<void> {
    this.log('📴 Hanging up call', callId ? `(ID: ${callId})` : '(Current)');

    let sessionToEnd = this.currentSession;
    
    // ✨ Multi-line: Find specific session if ID provided
    if (callId) {
      sessionToEnd = this.sessions.get(callId) || null;
    }

    // Fallback to current session if specific session not found or no ID provided
    if (!sessionToEnd) {
      if (callId) {
        this.log('⚠️ Session not found for ID:', callId, '- falling back to currentSession');
      }
      sessionToEnd = this.currentSession;
    }

    if (sessionToEnd) {
      try {
        switch (sessionToEnd.state) {
          case SessionState.Initial:
          case SessionState.Establishing:
            if (sessionToEnd instanceof Inviter) {
              await sessionToEnd.cancel();
            } else if (sessionToEnd instanceof Invitation) {
              await sessionToEnd.reject();
            }
            break;
          case SessionState.Established:
            await sessionToEnd.bye();
            break;
          default:
            this.log('⚠️ Session in unexpected state for hangup:', sessionToEnd.state);
            break;
        }
      } catch (error) {
        this.log('⚠️ Error during hangup:', error);
      }

      // Clean up
      this.sessions.delete(sessionToEnd.id);
      
      if (this.currentSession === sessionToEnd) {
        this.currentSession = null;
        // Only cleanup media if we are hanging up the active session
        // And if there are no other sessions? 
        // For now, let's be safe and cleanup if it was current.
        this.cleanupMedia();
      }
      
      this.log('✅ Hangup completed');
    } else {
      this.log('⚠️ No active session to hang up');
    }
  }

  /**
   * Get current session
   */
  public getCurrentSession(): Maybe<Inviter | Invitation> {
    return this.currentSession;
  }

  /**
   * Session event listeners
   */
  private setupSessionEventListeners(session: Inviter | Invitation): void {
    session.stateChange.addListener((state) => {
      this.log('Session state:', state);

      switch (state) {
        case SessionState.Establishing:
          this.log('Call establishing... Setting up early media');
          // 🎵 Setup remote media during establishing for early media (ringback, progress tones)
          this.setupRemoteMedia(session);
          break;
        case SessionState.Established:
          this.log('✅ Call established');
          // Re-setup media to ensure everything is connected
          this.setupRemoteMedia(session);
          // Start monitoring ICE connection
          this.startICEConnectionMonitoring(session);
          if (this.onCallEstablished) {
            this.onCallEstablished();
          }
          break;
        case SessionState.Terminated:
          this.log('Call terminated');
          // Stop ICE monitoring
          this.stopICEConnectionMonitoring();
          
          // Clean up session reference
          this.sessions.delete(session.id);
          
          if (this.currentSession === session) {
            this.currentSession = null;
            this.cleanupMedia();
          }
          
          if (this.onCallEnded) {
            this.onCallEnded();
          }
          this.log('✅ Session terminated, ready for next call');
          break;
      }
    });
  }

  /**
   * Remote media setup
   */
  private setupRemoteMedia(session: Inviter | Invitation): void {
    const sessionDescriptionHandler = session.sessionDescriptionHandler;
    
    if (!sessionDescriptionHandler) {
      this.log('⚠️ Session description handler not available yet');
      return;
    }
    
    if (sessionDescriptionHandler instanceof SessionDescriptionHandler) {
      const peerConnection = sessionDescriptionHandler.peerConnection;
      
      if (!peerConnection) {
        this.log('⚠️ Peer connection not available yet');
        return;
      }
      
      if (peerConnection) {
        // Setup ontrack handler for incoming media (early media & established)
        peerConnection.ontrack = (event) => {
          this.log('🎵 Remote track received:', event.track.kind, 'readyState:', event.track.readyState);
          
          if (event.track && event.streams && event.streams.length > 0) {
            const stream = event.streams[0];
            
            // Eğer zaten aynı stream çalıyorsa tekrar başlatma (AbortError önlemek için)
            if (this.remoteAudio.srcObject === stream) {
              this.log('⚠️ Stream already playing, skipping setup');
              return;
            }

            this.log('🎵 Setting remote audio stream');
            this.remoteAudio.srcObject = stream;
            // Sesin açık olduğundan emin ol
            this.remoteAudio.volume = 1.0;
            this.remoteAudio.muted = false;

            const playPromise = this.remoteAudio.play();
            if (playPromise !== undefined) {
              playPromise
                .then(() => this.log('✅ Remote audio playing'))
                .catch(e => {
                  // AbortError: Yeni bir play isteği geldiğinde eskisi iptal edilir, bu normaldir.
                  if (e.name === 'AbortError') {
                    this.log('ℹ️ Play request aborted (likely due to new stream/request)');
                  } else {
                    this.log('⚠️ Error playing audio:', e);
                  }
                });
            }
            
            if (this.onRemoteStream) {
              this.onRemoteStream(stream);
            }
          }
        };
        
        // Also check existing receivers
        peerConnection.getReceivers().forEach((receiver) => {
          if (receiver.track && receiver.track.kind === 'audio') {
            this.log('🎵 Found existing receiver track:', receiver.track.kind);
            
            // Mevcut stream'i kontrol et
            const currentStream = this.remoteAudio.srcObject as MediaStream;
            if (currentStream && currentStream.getTracks().some(t => t.id === receiver.track.id)) {
               this.log('⚠️ Track already in current stream, skipping');
               return;
            }

            const remoteStream = new MediaStream([receiver.track]);
            this.remoteAudio.srcObject = remoteStream;
            this.remoteAudio.play()
              .then(() => this.log('✅ Remote audio playing'))
              .catch(e => {
                if (e.name !== 'AbortError') {
                  this.log('⚠️ Error playing audio:', e);
                }
              });
            
            if (this.onRemoteStream) {
              this.onRemoteStream(remoteStream);
            }
          }
        });
      }
    }

    // Start RTP packet debugging
    this.startRTPDebug(session);
  }

  /**
   * Media cleanup
   */
  private cleanupMedia(): void {
    // Stop RTP debugging
    this.stopRTPDebug();
    
    // Stop ICE monitoring
    this.stopICEConnectionMonitoring();

    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
    }
    
    if (this.remoteAudio.srcObject) {
      this.remoteAudio.srcObject = null;
    }
  }

  /**
   * Mikrofonu mute/unmute
   */
  public setMuted(muted: boolean): void {
    if (this.currentSession) {
      const sessionDescriptionHandler = this.currentSession.sessionDescriptionHandler;
      
      if (sessionDescriptionHandler instanceof SessionDescriptionHandler) {
        const peerConnection = sessionDescriptionHandler.peerConnection;
        
        if (peerConnection) {
          peerConnection.getSenders().forEach((sender) => {
          if (sender.track && sender.track.kind === 'audio') {
            sender.track.enabled = !muted;
          }
        });
        }
        
        this.log(muted ? '🔇 Muted' : '🔊 Unmuted');
      }
    }
  }

  /**
   * Bağlantıyı kes
   */
  public async disconnect(): Promise<void> {
    this.log('Disconnecting...');
    
    // Set manual disconnect flag to prevent auto-reconnect
    this.isManualDisconnect = true;
    
    // Clear any pending reconnect timers
    if (this.reconnectInterval) {
      clearTimeout(this.reconnectInterval);
      this.reconnectInterval = null;
    }

    // Aktif çağrı varsa sonlandır
    if (this.currentSession) {
      await this.hangup();
    }

    // Kayıt iptal et ve dispose et
    if (this.registerer) {
      try {
        await this.registerer.unregister();
        this.registerer.dispose();
        this.registerer = null;
      } catch (error) {
        this.log('Error unregistering:', error);
      }
    }

    // UserAgent'ı durdur
    if (this.userAgent) {
      try {
        await this.userAgent.stop();
        this.userAgent = null;
      } catch (error) {
        this.log('Error stopping UserAgent:', error);
      }
    }

    this.cleanupMedia();
    this.log('Disconnected');
    this.log('⚠️ isManualDisconnect is now:', this.isManualDisconnect);
  }
  
  /**
   * Schedule reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    this.log('🔍 scheduleReconnect called');
    this.log('  - isManualDisconnect:', this.isManualDisconnect);
    this.log('  - reconnectAttempts:', this.reconnectAttempts);
    this.log('  - hasConfig:', !!this.config);
    
    // Clear any existing reconnect timer
    if (this.reconnectInterval) {
      this.log('  - Clearing existing reconnect timer');
      clearTimeout(this.reconnectInterval);
      this.reconnectInterval = null;
    }
    
    // Check if we've exceeded max attempts
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.log(`❌ Max reconnection attempts (${this.maxReconnectAttempts}) reached. Giving up.`);
      if (this.onUnregistered) {
        this.onUnregistered();
      }
      return;
    }
    
    // Calculate exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s max
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    
    this.log(`🔄 Scheduling reconnect attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts} in ${delay}ms...`);
    
    // Notify UI about reconnection attempt
    if (this.onReconnecting) {
      this.onReconnecting(this.reconnectAttempts + 1, this.maxReconnectAttempts);
    }
    
    this.reconnectInterval = setTimeout(() => {
      this.log('⏰ Reconnect timer fired');
      this.reconnectAttempts++;
      this.reconnect().catch(err => {
        this.log('❌ Reconnect promise rejected:', err);
      });
    }, delay) as unknown as number;
    
    this.log('✅ Reconnect timer set with id:', this.reconnectInterval);
  }
  
  /**
   * Attempt to reconnect
   */
  private async reconnect(): Promise<void> {
    this.log(`🔄 Reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} started...`);
    
    try {
      // Reset UserAgent and reconnect with stored config
      if (this.config) {
        // Cleanup existing resources first
        if (this.registerer) {
          try {
            this.registerer.dispose();
            this.log('✅ Disposed old registerer');
          } catch (e) {
            this.log('⚠️ Error disposing registerer:', e);
          }
          this.registerer = null;
        }
        
        if (this.userAgent) {
          try {
            await this.userAgent.stop();
            this.log('✅ Stopped old UserAgent');
          } catch (e) {
            this.log('⚠️ Error stopping UserAgent:', e);
          }
          this.userAgent = null;
        }
        
        // Reset manual disconnect flag for reconnection
        this.isManualDisconnect = false;
        
        // Reconnect with stored config
        await this.connect(this.config);
        this.log('✅ Reconnection successful');
      } else {
        this.log('❌ Cannot reconnect: No stored config');
        this.reconnectAttempts = this.maxReconnectAttempts; // Give up
      }
    } catch (error) {
      this.log(`❌ Reconnection attempt ${this.reconnectAttempts} failed:`, error);
      // Schedule next attempt
      this.scheduleReconnect();
    }
  }

  /**
   * Bağlantı durumu
   */
  public isConnected(): boolean {
    if (!this.userAgent || !this.registerer) {
      return false;
    }
    
    // Check if transport is connected and registerer is registered
    try {
      const transportConnected = this.userAgent.transport.state === 'Connected';
      const registered = this.registerer.state === RegistererState.Registered;
      return transportConnected && registered;
    } catch (error) {
      this.log('⚠️ Error checking connection state:', error);
      return false;
    }
  }

  /**
   * Manuel reconnect - UI'dan çağrılabilir
   */
  public async forceReconnect(): Promise<void> {
    this.log('🔄 Force reconnect requested');
    this.isManualDisconnect = false;
    this.reconnectAttempts = 0;
    
    if (!this.config) {
      this.log('❌ Cannot reconnect: No stored config');
      return;
    }
    
    // Cleanup and reconnect
    try {
      if (this.registerer) {
        this.registerer.dispose();
        this.registerer = null;
      }
      
      if (this.userAgent) {
        await this.userAgent.stop();
        this.userAgent = null;
      }
      
      await this.connect(this.config);
      this.log('✅ Force reconnect successful');
    } catch (error) {
      this.log('❌ Force reconnect failed:', error);
      throw error;
    }
  }

  /**
   * Aktif çağrı var mı
   */
  public hasActiveCall(): boolean {
    return this.currentSession !== null && 
           this.currentSession.state === SessionState.Established;
  }

  /**
   * Check if there is active RTP flow (audio packets)
   * Returns true if packets are flowing or connection is active with packets received
   */
  public async hasActiveRTPFlow(): Promise<boolean> {
    if (!this.currentSession) return false;
    
    // Check session state
    if (this.currentSession.state !== SessionState.Established && 
        this.currentSession.state !== SessionState.Establishing) {
      return false;
    }

    const sdh = this.currentSession.sessionDescriptionHandler;
    if (!(sdh instanceof SessionDescriptionHandler)) return false;
    
    const pc = sdh.peerConnection;
    if (!pc) return false;
    
    // Check connection state
    if (pc.connectionState === 'closed' || pc.connectionState === 'failed') {
      return false;
    }

    try {
      const stats = await pc.getStats();
      let inboundPackets = 0;
      let outboundPackets = 0;
      let hasAudioTrack = false;

      stats.forEach((stat) => {
        if (stat.type === 'inbound-rtp' && stat.kind === 'audio') {
          inboundPackets = (stat.packetsReceived || 0);
          hasAudioTrack = true;
        }
        if (stat.type === 'outbound-rtp' && stat.kind === 'audio') {
          outboundPackets = (stat.packetsSent || 0);
          hasAudioTrack = true;
        }
      });

      // If no audio tracks found, no flow
      if (!hasAudioTrack) return false;

      // Check for flow (delta)
      const hasFlow = (inboundPackets > this.lastInboundPackets) || (outboundPackets > this.lastOutboundPackets);
      
      // Update last values
      this.lastInboundPackets = inboundPackets;
      this.lastOutboundPackets = outboundPackets;
      this.lastRTPCheckTime = Date.now();

      // If we have packets and connection is connected, consider it active
      // (Even if delta is 0 due to VAD/Silence, but we have history of packets)
      if (pc.connectionState === 'connected' && (inboundPackets > 10 || outboundPackets > 10)) {
          return true;
      }

      return hasFlow;
    } catch (e) {
      console.error('Error checking RTP flow:', e);
      return false;
    }
  }

  /**
   * Reset RTP stats
   */
  public resetRTPStats(): void {
    this.lastInboundPackets = 0;
    this.lastOutboundPackets = 0;
    this.lastRTPCheckTime = 0;
  }

  /**
   * Start RTP debug logging
   */
  private startRTPDebug(session: Inviter | Invitation): void {
    if (this.rtpDebugInterval) {
      clearInterval(this.rtpDebugInterval);
    }

    this.log('🔍 Starting RTP packet debugging');

    this.rtpDebugInterval = window.setInterval(async () => {
      const sdh = session.sessionDescriptionHandler;
      if (!(sdh instanceof SessionDescriptionHandler)) {
        return;
      }

      const pc = sdh.peerConnection;
      if (!pc) {
        return;
      }

      try {
        const stats = await pc.getStats();
        stats.forEach((stat) => {
          if (stat.type === 'inbound-rtp' && stat.kind === 'audio') {
            this.log('📥 RTP Inbound:', {
              packetsReceived: stat.packetsReceived,
              packetsLost: stat.packetsLost,
              bytesReceived: stat.bytesReceived,
              jitter: stat.jitter,
              trackId: stat.trackId,
              ssrc: stat.ssrc
            });
          }
          if (stat.type === 'outbound-rtp' && stat.kind === 'audio') {
            this.log('📤 RTP Outbound:', {
              packetsSent: stat.packetsSent,
              bytesSent: stat.bytesSent,
              ssrc: stat.ssrc
            });
          }
        });
      } catch (error) {
        this.log('❌ Failed to get RTP stats:', error);
      }
    }, 2000); // Log every 2 seconds
  }

  /**
   * Stop RTP debug logging
   */
  private stopRTPDebug(): void {
    if (this.rtpDebugInterval) {
      clearInterval(this.rtpDebugInterval);
      this.rtpDebugInterval = null;
      this.log('⏹️ Stopped RTP debugging');
    }
  }

  /**
   * Start ICE connection monitoring
   */
  private startICEConnectionMonitoring(session: Inviter | Invitation): void {
    if (this.iceConnectionCheckInterval) {
      clearInterval(this.iceConnectionCheckInterval);
    }

    this.log('🔍 Starting ICE connection monitoring');

    const sdh = session.sessionDescriptionHandler;
    if (!(sdh instanceof SessionDescriptionHandler)) {
      this.log('⚠️ Session description handler not available for ICE monitoring');
      return;
    }

    const pc = sdh.peerConnection;
    if (!pc) {
      this.log('⚠️ PeerConnection not available for ICE monitoring');
      return;
    }

    // Monitor ICE connection state changes
    let iceDisconnectedTimeout: NodeJS.Timeout | null = null;
    
    pc.oniceconnectionstatechange = () => {
      const iceState = pc.iceConnectionState;
      this.log(`🧊 ICE Connection State: ${this.lastIceState} → ${iceState}`);
      
      if (iceState === 'disconnected') {
        // ⏱️ Disconnected geçici olabilir - 5 saniye bekle
        this.log('⚠️ ICE disconnected - waiting for recovery...');
        
        // Clear any existing timeout
        if (iceDisconnectedTimeout) {
          clearTimeout(iceDisconnectedTimeout);
        }
        
        // 5 saniye bekle - eğer hala disconnected ise ICE restart dene
        iceDisconnectedTimeout = setTimeout(() => {
          if (pc.iceConnectionState === 'disconnected') {
            this.log('⏰ ICE still disconnected after 5s - attempting restart...');
            if (pc.restartIce) {
              pc.restartIce();
            }
          }
        }, 5000);
        
      } else if (iceState === 'failed') {
        this.log('❌ ICE connection failed - attempting restart...');
        
        // Clear disconnect timeout
        if (iceDisconnectedTimeout) {
          clearTimeout(iceDisconnectedTimeout);
          iceDisconnectedTimeout = null;
        }
        
        // Try to restart ICE immediately on failure
        if (pc.restartIce) {
          this.log('🔄 Restarting ICE...');
          pc.restartIce();
        }
      } else if (iceState === 'closed') {
        this.log('❌ ICE connection closed');
        
        // Clear disconnect timeout
        if (iceDisconnectedTimeout) {
          clearTimeout(iceDisconnectedTimeout);
          iceDisconnectedTimeout = null;
        }
      } else if (iceState === 'connected' || iceState === 'completed') {
        this.log('✅ ICE connection stable');
        
        // Clear disconnect timeout - connection recovered
        if (iceDisconnectedTimeout) {
          clearTimeout(iceDisconnectedTimeout);
          iceDisconnectedTimeout = null;
        }
      }
      
      this.lastIceState = iceState;
    };

    // Monitor connection state changes (higher level)
    pc.onconnectionstatechange = () => {
      const connectionState = pc.connectionState;
      this.log(`🔌 Connection State: ${connectionState}`);
      
      if (connectionState === 'failed') {
        this.log('❌ PeerConnection failed - but will try to recover');
        // ⚠️ Failed durumu genellikle ICE restart ile düzelebilir
        // Hemen call failed olarak işaretlemiyoruz
        // NOT: Eğer gerçekten fail olduysa zaten hangup gelir
      } else if (connectionState === 'disconnected') {
        this.log('⚠️ PeerConnection disconnected - may recover');
        // Disconnected da geçici olabilir
      } else if (connectionState === 'closed') {
        this.log('❌ PeerConnection closed');
      } else if (connectionState === 'connected') {
        this.log('✅ PeerConnection connected');
      }
    };

    // Periodic ICE candidate check
    this.iceConnectionCheckInterval = window.setInterval(async () => {
      try {
        const stats = await pc.getStats();
        let activeCandidatePair: any = null;
        
        stats.forEach((stat) => {
          if (stat.type === 'candidate-pair' && stat.state === 'succeeded') {
            activeCandidatePair = stat;
          }
        });
        
        if (activeCandidatePair) {
          this.log('✅ Active ICE candidate pair:', {
            bytesSent: activeCandidatePair.bytesSent,
            bytesReceived: activeCandidatePair.bytesReceived,
            state: activeCandidatePair.state,
            nominated: activeCandidatePair.nominated
          });
        } else {
          this.log('⚠️ No active ICE candidate pair found');
        }
      } catch (error) {
        this.log('❌ Failed to check ICE candidates:', error);
      }
    }, 5000); // Check every 5 seconds
  }

  /**
   * Stop ICE connection monitoring
   */
  private stopICEConnectionMonitoring(): void {
    if (this.iceConnectionCheckInterval) {
      clearInterval(this.iceConnectionCheckInterval);
      this.iceConnectionCheckInterval = null;
      this.log('⏹️ Stopped ICE connection monitoring');
    }
  }

  /**
   * Get SDP for debugging
   */
  public getSDPDebug(): { local: string | null; remote: string | null } {
    if (!this.currentSession) {
      return { local: null, remote: null };
    }

    const sdh = this.currentSession.sessionDescriptionHandler;
    if (!(sdh instanceof SessionDescriptionHandler)) {
      return { local: null, remote: null };
    }

    const pc = sdh.peerConnection;
    if (!pc) {
      return { local: null, remote: null };
    }

    return {
      local: pc.localDescription?.sdp || null,
      remote: pc.remoteDescription?.sdp || null
    };
  }

  /**
   * Cleanup peer connection (stub for compatibility)
   */
  public cleanupPeerConnection(): void {
    this.log('🧹 Cleaning up peer connection');
    if (this.currentSession) {
      try {
        // Session cleanup is handled by hangup
        this.hangup();
      } catch (error) {
        this.log('Error during cleanup:', error);
      }
    }
  }

  /**
   * Unregister all queues (stub for compatibility)
   */
  public async unregisterAllQueues(): Promise<boolean> {
    this.log('📤 Unregister all queues (not implemented for FreePBX)');
    return true;
  }

  /**
   * Hold call (put on hold with music)
   */
  public async holdCall(): Promise<void> {
    this.log('⏸️ Putting call on hold');
    
    if (!this.currentSession || this.currentSession.state !== SessionState.Established) {
      this.log('⚠️ No active call to hold');
      return;
    }

    try {
      const sessionDescriptionHandler = this.currentSession.sessionDescriptionHandler;
      if (sessionDescriptionHandler instanceof SessionDescriptionHandler) {
        const peerConnection = sessionDescriptionHandler.peerConnection;
        
        if (peerConnection) {
          // Mute local audio tracks
          peerConnection.getSenders().forEach((sender) => {
            if (sender.track && sender.track.kind === 'audio') {
              sender.track.enabled = false;
              this.log('🔇 Muted audio sender');
            }
          });
          
          // Send re-INVITE with modified SDP for hold
          const inviteOptions: SessionInviteOptions = {
            sessionDescriptionHandlerModifiers: [
              (description: RTCSessionDescriptionInit): Promise<RTCSessionDescriptionInit> => {
                // Modify SDP to indicate hold (sendonly)
                if (description.sdp) {
                  description.sdp = description.sdp.replace(/a=sendrecv/g, 'a=sendonly');
                  this.log('📝 Modified SDP for hold (sendonly)');
                }
                return Promise.resolve(description);
              }
            ],
            requestDelegate: {
              onAccept: () => {
                this.log('✅ Call put on hold - MOH should play');
              },
              onReject: (response) => {
                this.log('❌ Hold request rejected:', response);
              }
            }
          };
          
          // Send re-INVITE
          await (this.currentSession as Inviter).invite(inviteOptions);
          this.log('📤 Re-INVITE sent for hold');
        }
      }
    } catch (error) {
      this.log('❌ Error putting call on hold:', error);
      throw error;
    }
  }

  /**
   * Unhold call (resume from hold)
   */
  public async unholdCall(): Promise<void> {
    this.log('▶️ Resuming call from hold');
    
    if (!this.currentSession || this.currentSession.state !== SessionState.Established) {
      this.log('⚠️ No active call to unhold');
      return;
    }

    try {
      const sessionDescriptionHandler = this.currentSession.sessionDescriptionHandler;
      if (sessionDescriptionHandler instanceof SessionDescriptionHandler) {
        const peerConnection = sessionDescriptionHandler.peerConnection;
        
        if (peerConnection) {
          // Re-enable outgoing audio (unmute microphone)
          peerConnection.getSenders().forEach((sender) => {
            if (sender.track && sender.track.kind === 'audio') {
              sender.track.enabled = true;
            }
          });
          
          // Modify SDP back to sendrecv to resume normal call
          const options = {
            sessionDescriptionHandlerModifiers: [
              (description: any) => {
                // Set audio back to sendrecv (normal two-way audio)
                description.sdp = description.sdp.replace(
                  /a=sendonly/g,
                  'a=sendrecv'
                );
                this.log('📝 Modified SDP for unhold (sendrecv)');
                return Promise.resolve(description);
              }
            ],
            requestDelegate: {
              onAccept: () => {
                this.log('✅ Call resumed - MOH stopped');
              },
              onReject: (response: any) => {
                this.log('❌ Unhold request rejected:', response);
              }
            }
          };
          
          // Send re-INVITE with modified SDP
          await (this.currentSession as any).invite(options);
        }
      }
    } catch (error) {
      this.log('❌ Error resuming call:', error);
      throw error;
    }
  }

  /**
   * Send DTMF (Dual-Tone Multi-Frequency) digit during active call
   * Used for IVR navigation, PIN entry, etc.
   */
  public async sendDTMF(digit: string): Promise<void> {
    if (!this.currentSession || this.currentSession.state !== SessionState.Established) {
      this.log('⚠️ No active call to send DTMF');
      return;
    }

    try {
      const sessionDescriptionHandler = this.currentSession.sessionDescriptionHandler;
      if (!(sessionDescriptionHandler instanceof SessionDescriptionHandler)) {
        this.log('⚠️ Session description handler not available');
        return;
      }

      const peerConnection = sessionDescriptionHandler.peerConnection;
      if (!peerConnection) {
        this.log('⚠️ Peer connection not available');
        return;
      }

      // Get audio sender
      const audioSender = peerConnection.getSenders().find(sender => sender.track?.kind === 'audio');
      if (!audioSender) {
        this.log('⚠️ No audio sender found');
        return;
      }

      // Use DTMFSender if available (RFC 4733)
      const dtmfSender = (audioSender as any).dtmf;
      if (dtmfSender) {
        try {
          dtmfSender.insertDTMF(digit, 100, 50); // digit, duration (ms), interToneGap (ms)
          this.log(`📞 DTMF sent: ${digit}`);
        } catch (error) {
          this.log(`❌ Error sending DTMF via DTMFSender: ${error}`);
          // Fallback to INFO method
          await this.sendDTMFViaINFO(digit);
        }
      } else {
        // Fallback to SIP INFO method (RFC 2976)
        await this.sendDTMFViaINFO(digit);
      }
    } catch (error) {
      this.log(`❌ Error sending DTMF: ${error}`);
    }
  }

  /**
   * Send DTMF via SIP INFO method (RFC 2976)
   * Fallback method when DTMFSender is not available
   */
  private async sendDTMFViaINFO(digit: string): Promise<void> {
    if (!this.currentSession) {
      this.log('⚠️ No active session for INFO DTMF');
      return;
    }

    try {
      // Map digit to DTMF code
      const dtmfCodes: { [key: string]: number } = {
        '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
        '*': 10, '#': 11
      };

      const code = dtmfCodes[digit];
      if (code === undefined) {
        this.log(`⚠️ Invalid DTMF digit: ${digit}`);
        return;
      }

      // Create INFO request with DTMF payload
      const infoRequest = (this.currentSession as any).info({
        body: {
          contentType: 'application/dtmf-relay',
          body: `Signal=${digit}\r\nDuration=100`
        }
      });

      this.log(`📞 DTMF sent via INFO: ${digit}`);
    } catch (error) {
      this.log(`❌ Error sending DTMF via INFO: ${error}`);
    }
  }
}

// Singleton instance
const freePBXWebRTCService = new FreePBXWebRTCService();
export default freePBXWebRTCService;
